export function supportsHoverMediaQuery(): boolean {
  if (!window.matchMedia) {
    return false;
  }
  try {
    return window.matchMedia("(hover: hover)").matches;
  } catch (e) {
    return false;
  }
}

export function bindPlayerInteractionEvents(
  element: HTMLElement,
  handlers: {
    onMove?: () => void;
    onDown?: (event: Event) => void;
  }
): () => void {
  var onMove = handlers.onMove;
  var onDown = handlers.onDown;

  var moveHandler = function () {
    if (onMove) {
      onMove();
    }
  };

  var downHandler = function (event: Event) {
    if (onDown) {
      onDown(event);
    }
  };

  element.addEventListener("mousemove", moveHandler, false);
  element.addEventListener("mousedown", downHandler, false);
  element.addEventListener("touchmove", moveHandler, false);
  element.addEventListener("touchstart", downHandler, false);

  return function () {
    element.removeEventListener("mousemove", moveHandler, false);
    element.removeEventListener("mousedown", downHandler, false);
    element.removeEventListener("touchmove", moveHandler, false);
    element.removeEventListener("touchstart", downHandler, false);
  };
}

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
  mozCancelFullScreen?: () => void;
  msExitFullscreen?: () => void;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => void;
  mozRequestFullScreen?: () => void;
  msRequestFullscreen?: () => void;
};

export function getFullscreenElement(): Element | null {
  var doc = document as FullscreenDocument;
  if (doc.fullscreenElement) {
    return doc.fullscreenElement;
  }
  if (doc.webkitFullscreenElement) {
    return doc.webkitFullscreenElement;
  }
  if (doc.mozFullScreenElement) {
    return doc.mozFullScreenElement;
  }
  if (doc.msFullscreenElement) {
    return doc.msFullscreenElement;
  }
  return null;
}

export function requestElementFullscreen(element: HTMLElement): void {
  var el = element as FullscreenElement;
  if (el.requestFullscreen) {
    el.requestFullscreen();
    return;
  }
  if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
    return;
  }
  if (el.mozRequestFullScreen) {
    el.mozRequestFullScreen();
    return;
  }
  if (el.msRequestFullscreen) {
    el.msRequestFullscreen();
  }
}

export function exitDocumentFullscreen(): void {
  var doc = document as FullscreenDocument;
  if (doc.exitFullscreen) {
    doc.exitFullscreen();
    return;
  }
  if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
    return;
  }
  if (doc.mozCancelFullScreen) {
    doc.mozCancelFullScreen();
    return;
  }
  if (doc.msExitFullscreen) {
    doc.msExitFullscreen();
  }
}

export function toggleElementFullscreen(element: HTMLElement): void {
  if (getFullscreenElement()) {
    exitDocumentFullscreen();
    return;
  }
  requestElementFullscreen(element);
}

export function onFullscreenChange(callback: () => void): () => void {
  var handler = function () {
    callback();
  };

  document.addEventListener("fullscreenchange", handler, false);
  document.addEventListener("webkitfullscreenchange", handler, false);
  document.addEventListener("mozfullscreenchange", handler, false);
  document.addEventListener("MSFullscreenChange", handler, false);

  return function () {
    document.removeEventListener("fullscreenchange", handler, false);
    document.removeEventListener("webkitfullscreenchange", handler, false);
    document.removeEventListener("mozfullscreenchange", handler, false);
    document.removeEventListener("MSFullscreenChange", handler, false);
  };
}

export function isFiniteNumber(value: unknown): boolean {
  return typeof value === "number" && isFinite(value);
}

export function padTwoDigits(value: number): string {
  return value < 10 ? "0" + value : String(value);
}
